import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { TableSkeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';
import { Pagination } from '../components/ui/Pagination';

interface SekolahRow {
  id: number;
  nama: string;
  jenjang: string;
  npsn: string | null;
  status: 'negeri' | 'swasta';
  alamat: string | null;
  is_active: boolean;
  total_siswa: number;
  kelas: string[];
}

interface SekolahListResult {
  items: SekolahRow[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

// Backend masih mewajibkan jenjang & status. Form tidak menampilkannya;
// untuk sekolah baru nilainya ditebak dari nama (SD/SMP/SMA/MTs/MA/dst),
// untuk sekolah yang diubah nilai lamanya dipertahankan.
const inferJenjang = (nama: string): string => {
  const n = nama.toUpperCase();
  if (/\bSMKN?\b/.test(n)) return 'SMK';
  if (/\bSMAN?\b/.test(n)) return 'SMA';
  if (/\bSMPN?\b/.test(n)) return 'SMP';
  if (/\bMTSN?\b/.test(n)) return 'MTs';
  if (/\bMAN?\b/.test(n)) return 'MA';
  if (/\bMIN?\b/.test(n)) return 'MI';
  if (/\bSDN?\b/.test(n)) return 'SD';
  return 'SMP';
};

const inferStatus = (nama: string): 'negeri' | 'swasta' =>
  /NEGERI|\b(SDN|SMPN|SMAN|SMKN|MTSN|MIN|MAN)\b/i.test(nama) ? 'negeri' : 'swasta';

const emptyForm = {
  nama: '',
  kelas: [] as string[],
};

export function SekolahFormalPage() {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const canCreate = hasPermission('kelas', 'tambah');
  const canEdit = hasPermission('kelas', 'ubah');
  const canDelete = hasPermission('kelas', 'hapus');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SekolahRow | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [kelasInput, setKelasInput] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sekolah-formal', search, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get<SekolahListResult>(`/api/sekolah-formal?${params.toString()}`);
      return res.data;
    },
  });

  const list = data?.items || [];
  const pagination = data?.pagination;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['sekolah-formal'] });
    queryClient.invalidateQueries({ queryKey: ['sekolah-formal-simple'] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post('/api/sekolah-formal', payload),
    onSuccess: () => {
      refresh();
      success('Sekolah formal berhasil ditambahkan.');
      setModalOpen(false);
    },
    onError: (err: any) => error(err.message || 'Gagal menambahkan sekolah formal.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      api.put(`/api/sekolah-formal/${id}`, payload),
    onSuccess: () => {
      refresh();
      success('Sekolah formal berhasil diperbarui.');
      setModalOpen(false);
    },
    onError: (err: any) => error(err.message || 'Gagal memperbarui sekolah formal.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/sekolah-formal/${id}`),
    onSuccess: () => {
      refresh();
      success('Sekolah formal berhasil dihapus.');
      setDeleteId(null);
    },
    onError: (err: any) => {
      error(err.message || 'Gagal menghapus sekolah formal.');
      setDeleteId(null);
    },
  });

  const addKelas = (raw: string) => {
    const items = raw
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    if (items.length === 0) return;
    setFormData((prev) => {
      const seen = new Set(prev.kelas.map((k) => k.toLowerCase()));
      const merged = [...prev.kelas];
      for (const k of items) {
        if (!seen.has(k.toLowerCase())) {
          seen.add(k.toLowerCase());
          merged.push(k);
        }
      }
      return { ...prev, kelas: merged };
    });
    setKelasInput('');
  };

  const removeKelas = (k: string) => {
    setFormData((prev) => ({ ...prev, kelas: prev.kelas.filter((x) => x !== k) }));
  };

  const handleOpenModal = (item?: SekolahRow) => {
    setKelasInput('');
    if (item) {
      setEditingItem(item);
      setFormData({
        nama: item.nama,
        kelas: item.kelas || [],
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nama = formData.nama.trim();
    if (nama.length < 3) {
      error('Nama sekolah minimal 3 karakter.');
      return;
    }
    const pending = kelasInput
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    const payload = {
      nama,
      kelas: Array.from(new Set([...formData.kelas, ...pending])),
      is_active: editingItem?.is_active ?? true,
      // Field wajib backend yang tidak ditampilkan di form
      jenjang: editingItem ? editingItem.jenjang : inferJenjang(nama),
      status: editingItem ? editingItem.status : inferStatus(nama),
      npsn: editingItem?.npsn || '',
      alamat: editingItem?.alamat || '',
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Sekolah Formal</h2>
          <p className="text-xs text-slate-500 mt-1">
            Daftar sekolah formal beserta kelasnya. Dipilih di form Data Siswa.
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            Tambah Sekolah
          </button>
        )}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Cari nama sekolah..."
          className="w-full pl-10 pr-3.5 py-2.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-emerald-600"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={5} cols={3} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="Belum Ada Sekolah Formal"
            description="Tambahkan sekolah formal beserta daftar kelasnya."
            onAction={canCreate ? () => handleOpenModal() : undefined}
            actionText="Tambah Sekolah"
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[10px] sm:text-xs">
                <thead className="bg-slate-50 border-b border-slate-200/80 text-slate-600 font-semibold uppercase tracking-tight sm:tracking-wider text-[9px] sm:text-[11px]">
                  <tr>
                    <th className="py-3 pl-4 pr-1.5 sm:py-3.5 sm:px-4">Nama Sekolah</th>
                    <th className="py-3 px-1.5 sm:py-3.5 sm:px-4">Kelas</th>
                    <th className="py-3 px-1.5 sm:py-3.5 sm:px-4 text-center">
                      <span className="sm:hidden">Siswa</span>
                      <span className="hidden sm:inline">Siswa Aktif</span>
                    </th>
                    <th className="py-3 px-2 sm:py-3.5 sm:px-4 text-center">Aksi</th>
                  </tr>
  </thead>
  <tbody className="divide-y divide-slate-100 text-slate-700">
    {list.map((item) => (
      <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
        <td className="py-2.5 pl-4 pr-1.5 sm:py-3.5 sm:px-4 font-semibold text-slate-900 leading-snug">
          {item.nama}
        </td>
        <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4">
          {item.kelas && item.kelas.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {item.kelas.slice(0, 4).map((k) => (
                <span
                  key={k}
                  className="px-1.5 sm:px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold whitespace-nowrap"
                >
                  {k}
                </span>
              ))}
              {item.kelas.length > 4 && (
                <span
                  className="px-1.5 sm:px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 font-semibold"
                  title={item.kelas.slice(4).join(', ')}
                >
                  +{item.kelas.length - 4}
                </span>
              )}
            </div>
          ) : (
            <span className="text-slate-400">-</span>
          )}
        </td>
        <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 font-semibold text-center">
  {item.total_siswa}
</td>
        <td className="py-2.5 px-2 sm:py-3.5 sm:px-4 text-center">
  <div className="flex items-center justify-center gap-0 sm:gap-1.5">
            {canEdit && (
              <button
                onClick={() => handleOpenModal(item)}
                className="p-1 sm:p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                title="Ubah"
              >
                <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setDeleteId(item.id)}
                className="p-1 sm:p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                title="Hapus"
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
    ))}
  </tbody>
</table>
            </div>

            {pagination && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={(p) => setPage(p)}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? 'Ubah Sekolah Formal' : 'Tambah Sekolah Formal'}
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Nama Sekolah
            </label>
            <input
              type="text"
              value={formData.nama}
              onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
              placeholder="Contoh: SMP Al Munawwariyyah"
              className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Daftar Kelas
            </label>
            {formData.kelas.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {formData.kelas.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
                  >
                    {k}
                    <button
                      type="button"
                      onClick={() => removeKelas(k)}
                      className="p-0.5 rounded hover:bg-slate-200 text-slate-500 cursor-pointer"
                      title="Hapus kelas"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={kelasInput}
                onChange={(e) => setKelasInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addKelas(kelasInput);
                  }
                }}
                placeholder="Ketik nama kelas lalu Enter, contoh: 7A"
                className="flex-1 px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              />
              <button
                type="button"
                onClick={() => addKelas(kelasInput)}
                className="px-3.5 py-2 text-xs font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-xl cursor-pointer"
              >
                Tambah
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Semua kelas satu sekolah dimasukkan di sini, jangan buat baris sekolah baru per kelas.
            </p>
          </div>

          <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 rounded-xl transition-colors cursor-pointer"
            >
              {editingItem ? 'Simpan Perubahan' : 'Tambah'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Hapus Sekolah Formal"
        message="Sekolah yang masih dipakai data siswa tidak dapat dihapus. Pindahkan atau ubah data siswanya dulu."
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}